import React, { useEffect, useRef, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import {
  Plus, Trash2, Save, X, GripVertical, FileType, FolderOpen, FileSymlink, Search,
  AlertTriangle, ArrowRight, CheckCircle, Clock, Check, XCircle, Copy,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import SearchableSelect from "@/components/SearchableSelect";
import { useAuth } from "@/contexts/AuthContext";

const blankType = {
  name: "",
  description: "",
  folder_template: "/{type}/{year}",
  filename_template: "{type}_{date}_{original}",
  fields: [],
};

const STATUS_CONFIG = {
  active:  { label: "Actif",      color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  pending: { label: "En attente", color: "bg-amber-100 text-amber-700 border-amber-200" },
  rejected:{ label: "Refusé",     color: "bg-red-100 text-red-600 border-red-200" },
};

export default function DocumentTypes() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const [types, setTypes] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(blankType);
  const folderRef = useRef(null);
  const filenameRef = useRef(null);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // "all" | "active" | "pending"
  const [migration, setMigration] = useState(null);
  const [migrationLoading, setMigrationLoading] = useState(false);

  const load = async () => {
    try {
      const [t, c] = await Promise.all([
        api.get("/document-types"),
        api.get("/metadata-catalog"),
      ]);
      setTypes(t.data);
      setCatalog(c.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Computed counts for filter cards
  const counts = {
    all: types.length,
    active: types.filter((t) => t.status === "active").length,
    pending: types.filter((t) => t.status === "pending").length,
  };

  const openNew = () => { setForm(blankType); setSelectedId("new"); };

  const openEdit = (t) => {
    // Non-admin can only edit their own pending types
    if (!isAdmin && (t.status !== "pending" || t.proposed_by !== user?.id)) return;
    setForm({
      name: t.name,
      description: t.description || "",
      folder_template: t.folder_template,
      filename_template: t.filename_template,
      fields: t.fields
        .filter((f) => f.catalog_id)
        .map((f) => ({ catalog_id: f.catalog_id, required: f.required, ordre: f.ordre })),
    });
    setSelectedId(t.id);
  };

  const closePanel = () => setSelectedId(null);

  const addField = () =>
    setForm((f) => ({
      ...f,
      fields: [...f.fields, { catalog_id: "", required: true, ordre: f.fields.length }],
    }));

  const updateField = (i, patch) =>
    setForm((f) => ({ ...f, fields: f.fields.map((fd, idx) => (idx === i ? { ...fd, ...patch } : fd)) }));

  const removeField = (i) =>
    setForm((f) => ({ ...f, fields: f.fields.filter((_, idx) => idx !== i) }));

  const moveField = (i, dir) =>
    setForm((f) => {
      const j = i + dir;
      if (j < 0 || j >= f.fields.length) return f;
      const arr = [...f.fields];
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, fields: arr };
    });

  const makeInserter = (fieldKey, inputRef, sep) => (key) => {
    if (!key) return;
    const token = `{${key}}`;
    const input = inputRef.current;
    setForm((f) => {
      const current = f[fieldKey] || "";
      if (current.includes(token)) {
        let next = current.split(token).join("");
        if (sep === "/") {
          next = next.replace(/\/{2,}/g, "/");
          if (next.length > 1 && next.endsWith("/")) next = next.slice(0, -1);
          if (next === "") next = "/";
        } else {
          next = next.replace(/_{2,}/g, "_").replace(/-{2,}/g, "-");
          next = next.replace(/^[_\-]+|[_\-]+$/g, "");
        }
        toast.success(`${token} retiré.`);
        return { ...f, [fieldKey]: next };
      }
      const pos =
        input && document.activeElement === input
          ? (input.selectionStart ?? current.length)
          : current.length;
      const before = current.slice(0, pos);
      const after = current.slice(pos);
      const addSep =
        sep === "/"
          ? before !== "" && !before.endsWith("/") ? "/" : ""
          : before !== "" && !/[\/_\-\s]$/.test(before) ? "_" : "";
      const addSepAfter =
        sep === "/"
          ? after !== "" && !after.startsWith("/") ? "/" : ""
          : after !== "" && !/^[\/_\-\s]/.test(after) ? "_" : "";
      const result = before + addSep + token + addSepAfter + after;
      const newCursor = pos + addSep.length + token.length;
      setTimeout(() => {
        if (input) { input.focus(); input.setSelectionRange(newCursor, newCursor); }
      }, 0);
      toast.success(`${token} inséré.`);
      return { ...f, [fieldKey]: result };
    });
  };

  const insertFolder = makeInserter("folder_template", folderRef, "/");
  const insertFilename = makeInserter("filename_template", filenameRef, "_");

  const save = async () => {
    if (!selectedId) return;
    if (!form.name.trim()) { toast.error("Le nom est obligatoire."); return; }
    const valid = form.fields.filter((fd) => fd.catalog_id);
    if (valid.length !== form.fields.length) {
      toast.error("Chaque ligne doit avoir une métadonnée sélectionnée."); return;
    }
    const keys = valid.map((v) => v.catalog_id);
    if (new Set(keys).size !== keys.length) {
      toast.error("Une métadonnée ne peut être ajoutée qu'une seule fois par type."); return;
    }

    const payload = {
      name: form.name,
      description: form.description,
      folder_template: form.folder_template,
      filename_template: form.filename_template,
      fields: valid.map((fd, idx) => ({ catalog_id: fd.catalog_id, required: fd.required, ordre: idx })),
    };

    if (selectedId !== "new") {
      const existing = types.find((t) => t.id === selectedId);
      const folderChanged = existing && existing.folder_template !== form.folder_template;
      const filenameChanged = existing && existing.filename_template !== form.filename_template;

      if (isAdmin && (folderChanged || filenameChanged)) {
        setMigrationLoading(true);
        try {
          const params = new URLSearchParams({ new_folder_template: form.folder_template });
          if (filenameChanged) params.set("new_filename_template", form.filename_template);
          const { data: preview } = await api.get(
            `/document-types/${selectedId}/migration-preview?${params}`
          );
          if (preview.affected_count > 0) {
            setMigration({ preview, payload });
            return;
          }
        } catch (_) {
          // preview non critique
        } finally {
          setMigrationLoading(false);
        }
      }
    }

    await _performSave(payload, false);
  };

  const _performSave = async (payload, migrate) => {
    try {
      if (selectedId === "new") {
        await api.post("/document-types", payload);
        if (!isAdmin) {
          toast.success("Type soumis pour validation. Un administrateur va le vérifier.");
        } else {
          toast.success("Type créé.");
        }
      } else {
        await api.put(`/document-types/${selectedId}`, payload);
        if (migrate) {
          const { data } = await api.post(`/document-types/${selectedId}/migrate-paths`);
          const skippedMsg = data.skipped > 0 ? ` (${data.skipped} ignoré(s) — métadonnées incomplètes)` : "";
          toast.success(`Type mis à jour — ${data.migrated} document(s) recalculé(s).${skippedMsg}`);
        } else {
          toast.success("Type mis à jour.");
        }
      }
      setMigration(null);
      setSelectedId(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer ce type de document ?")) return;
    try {
      await api.delete(`/document-types/${id}`);
      if (selectedId === id) closePanel();
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };


  const duplicate = (t, e) => {
    e.stopPropagation();
    setForm({
      name: `Copie de ${t.name}`,
      description: t.description || "",
      folder_template: t.folder_template,
      filename_template: t.filename_template,
      fields: t.fields
        .filter((f) => f.catalog_id)
        .map((f) => ({ catalog_id: f.catalog_id, required: f.required, ordre: f.ordre })),
    });
    setSelectedId("new");
  };


  const approve = async (id, e) => {    e.stopPropagation();
    try {
      await api.post(`/document-types/${id}/approve`);
      toast.success("Type de document approuvé.");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const reject = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Refuser ce type de document ?")) return;
    try {
      await api.post(`/document-types/${id}/reject`);
      toast.success("Type de document refusé.");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const catalogItems = catalog.map((c) => ({ value: c.id, label: c.label }));
  const panelOpen = selectedId !== null;

  // Filtered list
  const filteredTypes = (() => {
    const q = search.trim().toLowerCase();
    let list = types;
    if (filter === "active") list = list.filter((t) => t.status === "active");
    else if (filter === "pending") list = list.filter((t) => t.status === "pending");
    if (q) list = list.filter(
      (t) => t.name.toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q)
    );
    return list;
  })();

  // Can a user click to edit a card?
  const canEdit = (t) => isAdmin || (t.status === "pending" && t.proposed_by === user?.id);

  return (
    <div className="min-h-screen">
      <PageHeader
        title="Types de documents"
        subtitle="Définissez les types de documents et leurs modèles de classement"
        index="05"
      >
        <button
          data-testid="type-new-btn"
          onClick={openNew}
          className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium px-4 py-2 transition-colors"
        >
          <Plus size={14} strokeWidth={1.5} />
          {isAdmin ? "Nouveau type" : "Proposer un type"}
        </button>
      </PageHeader>

      <div className="p-6 md:p-10">

        {/* ── Filter cards ── */}
        <div className="flex gap-3 mb-6 flex-wrap">
          {[
            { key: "all",     label: "Tous",        count: counts.all,     icon: FileSymlink },
            { key: "active",  label: "Actifs",      count: counts.active,  icon: CheckCircle },
            { key: "pending", label: "En attente",  count: counts.pending, icon: Clock },
          ].map(({ key, label, count, icon: Icon }) => (
            <button
              key={key}
              data-testid={`filter-${key}`}
              onClick={() => setFilter(key)}
              className={`flex items-center gap-3 px-4 py-3 border text-sm font-medium transition-all min-w-[130px] ${
                filter === key
                  ? "border-[#002FA7] bg-[#002FA7] text-white"
                  : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400"
              }`}
            >
              <Icon size={14} strokeWidth={1.5} className="shrink-0" />
              <span className="flex-1 text-left">{label}</span>
              <span className={`text-xs font-mono tabular-nums px-1.5 py-0.5 ${
                filter === key ? "bg-white/20 text-white" : "bg-zinc-100 text-zinc-500"
              }`}>
                {count}
              </span>
            </button>
          ))}
        </div>

        {/* ── Search ── */}
        <div className="relative mb-6 max-w-sm">
          <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <input
            data-testid="types-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un type…"
            className="w-full border border-zinc-300 pl-9 pr-4 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
          />
        </div>

        {loading ? (
          <div className="text-sm text-zinc-500">Chargement…</div>
        ) : filteredTypes.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-16 text-center">
            <FileSymlink size={32} strokeWidth={1} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-sm text-zinc-500">
              {search.trim() ? `Aucun résultat pour « ${search} ».` : "Aucun type de document dans cette catégorie."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredTypes.map((t) => {
              const statusCfg = STATUS_CONFIG[t.status] || STATUS_CONFIG.active;
              const editable = canEdit(t);
              return (
                <div
                  key={t.id}
                  data-testid={`type-card-${t.id}`}
                  onClick={() => editable && openEdit(t)}
                  className={`border bg-white p-5 transition-all group ${
                    editable ? "cursor-pointer" : "cursor-default"
                  } ${
                    selectedId === t.id
                      ? "border-l-4 border-[#002FA7] shadow-sm"
                      : "border-zinc-200 border-l-4 border-l-transparent hover:border-l-[#002FA7] hover:border-zinc-300"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-zinc-900 truncate">{t.name}</div>
                        {t.status !== "active" && (
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 border shrink-0 ${statusCfg.color}`}>
                            {statusCfg.label}
                          </span>
                        )}
                      </div>
                      {t.description && (
                        <div className="text-xs text-zinc-500 mt-0.5 truncate">{t.description}</div>
                      )}
                      {t.status === "pending" && t.proposed_by_name && isAdmin && (
                        <div className="text-[10px] text-zinc-400 font-mono mt-1">
                          Proposé par {t.proposed_by_name}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      {/* Admin approve/reject on pending */}
                      {isAdmin && t.status === "pending" && (
                        <>
                          <button
                            data-testid={`type-approve-${t.id}`}
                            onClick={(e) => approve(t.id, e)}
                            title="Approuver"
                            className="p-1.5 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                          >
                            <Check size={14} strokeWidth={2} />
                          </button>
                          <button
                            data-testid={`type-reject-${t.id}`}
                            onClick={(e) => reject(t.id, e)}
                            title="Refuser"
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <XCircle size={14} strokeWidth={1.5} />
                          </button>
                        </>
                      )}
                      {/* Duplicate button — admin only, always visible on hover */}
                      {isAdmin && (
                        <button
                          data-testid={`type-duplicate-${t.id}`}
                          onClick={(e) => duplicate(t, e)}
                          title="Dupliquer"
                          className="p-1.5 text-zinc-300 hover:text-[#002FA7] opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Copy size={13} strokeWidth={1.5} />
                        </button>
                      )}
                      {/* Delete button */}
                      {(isAdmin || (t.status === "pending" && t.proposed_by === user?.id)) && (
                        <button
                          data-testid={`type-delete-${t.id}`}
                          onClick={(e) => remove(t.id, e)}
                          className="p-1.5 text-zinc-300 hover:text-[#DC2626] opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 size={13} strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 space-y-1">
                    <div className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500">
                      <FolderOpen size={10} strokeWidth={1.5} className="shrink-0" />
                      <span className="truncate">{t.folder_template}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500">
                      <FileType size={10} strokeWidth={1.5} className="shrink-0" />
                      <span className="truncate">{t.filename_template}</span>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {t.fields.slice(0, 4).map((f) => (
                      <span key={f.id} className="text-[10px] font-mono px-1.5 py-0.5 bg-zinc-100 text-zinc-600">
                        {f.key}{f.required && <span className="text-[#FF3B30]">*</span>}
                      </span>
                    ))}
                    {t.fields.length > 4 && (
                      <span className="text-[10px] font-mono px-1.5 py-0.5 bg-zinc-100 text-zinc-400">
                        +{t.fields.length - 4}
                      </span>
                    )}
                    {t.fields.length === 0 && (
                      <span className="text-[10px] text-zinc-400 italic">aucune métadonnée</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Backdrop */}
      {panelOpen && (
        <div className="fixed inset-0 bg-black/25 z-40" onClick={closePanel} />
      )}

      {/* ═══ SIDE PANEL ═══ */}
      <div
        data-testid="type-editor"
        className={`fixed right-0 top-0 h-screen z-50 flex flex-col bg-white border-l border-zinc-200 shadow-2xl transition-transform duration-300 ease-in-out w-[910px] max-w-[96vw] ${
          panelOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-7 py-4 bg-zinc-900 text-white shrink-0">
          <div>
            <div className="text-[9px] uppercase tracking-[0.25em] font-mono text-zinc-300">
              {selectedId === "new"
                ? (isAdmin ? "Nouveau type" : "Proposer un type")
                : "Modifier le type"}
            </div>
            <div className="text-base font-semibold mt-0.5 truncate max-w-[560px]">
              {selectedId === "new" ? "—" : form.name || "—"}
            </div>
          </div>
          <button
            data-testid="type-cancel-btn"
            onClick={closePanel}
            className="p-2 text-zinc-400 hover:text-white transition-colors"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        {/* Non-admin notice */}
        {!isAdmin && (
          <div className="px-7 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2 text-xs text-amber-700">
            <Clock size={13} strokeWidth={1.5} className="shrink-0" />
            Votre proposition sera soumise à validation par un administrateur avant d&apos;être active.
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto divide-y divide-zinc-100">
          {/* Zone 01 */}
          <section className="px-7 py-6">
            <ZoneTitle number="01" label="Nom & Description" />
            <div className="mt-5 space-y-3">
              <InlineField label="Nom">
                <input
                  data-testid="type-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  maxLength={120}
                  placeholder="ex : Contrat de travail"
                  className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
                />
              </InlineField>
              <InlineField label="Description">
                <input
                  data-testid="type-desc-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  maxLength={500}
                  className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
                />
              </InlineField>
            </div>
          </section>

          {/* Zone 02 */}
          <section className="px-7 py-6">
            <ZoneTitle number="02" label="Chemin du dossier" />
            <div className="mt-5">
              <input
                ref={folderRef}
                data-testid="type-folder-input"
                value={form.folder_template}
                onChange={(e) => setForm({ ...form, folder_template: e.target.value })}
                maxLength={400}
                placeholder="/{type}/{year}"
                className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
              />
              <PlaceholderChips
                label="Standards"
                tokens={[
                  { key: "type", label: "Type" },
                  { key: "year", label: "Année" },
                  { key: "month", label: "Mois" },
                  { key: "date", label: "Date" },
                ]}
                active={form.folder_template}
                onToggle={insertFolder}
                testPrefix="folder-std"
              />
              <PlaceholderChips
                label="Métadonnées du catalogue"
                tokens={catalog.map((c) => ({ key: c.key, label: c.label }))}
                active={form.folder_template}
                onToggle={insertFolder}
                testPrefix="folder-meta"
              />
            </div>
          </section>

          {/* Zone 03 */}
          <section className="px-7 py-6">
            <ZoneTitle number="03" label="Nommage du fichier" />
            <div className="mt-5">
              <input
                ref={filenameRef}
                data-testid="type-filename-input"
                value={form.filename_template}
                onChange={(e) => setForm({ ...form, filename_template: e.target.value })}
                maxLength={400}
                className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
              />
              <PlaceholderChips
                label="Standards"
                tokens={[
                  { key: "type", label: "Type" },
                  { key: "year", label: "Année" },
                  { key: "month", label: "Mois" },
                  { key: "date", label: "Date" },
                  { key: "original", label: "Nom original" },
                ]}
                active={form.filename_template}
                onToggle={insertFilename}
                testPrefix="file-std"
              />
              <PlaceholderChips
                label="Métadonnées du catalogue"
                tokens={catalog.map((c) => ({ key: c.key, label: c.label }))}
                active={form.filename_template}
                onToggle={insertFilename}
                testPrefix="file-meta"
              />
            </div>
          </section>

          {/* Zone 04 */}
          <section className="px-7 py-6">
            <div className="flex items-center justify-between">
              <ZoneTitle number="04" label="Métadonnées rattachées" />
              <button
                data-testid="type-add-field-btn"
                onClick={addField}
                className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline"
              >
                <Plus size={12} strokeWidth={2} /> Ajouter
              </button>
            </div>
            <div className="mt-5 space-y-2">
              {form.fields.length === 0 && (
                <p className="text-xs text-zinc-400 italic">
                  Cliquez sur « Ajouter » pour rattacher des métadonnées.
                </p>
              )}
              {form.fields.map((f, i) => (
                <div key={i} className="flex items-center gap-4" data-testid={`type-field-row-${i}`}>
                  <button
                    type="button"
                    onClick={() => moveField(i, -1)}
                    className="text-zinc-400 hover:text-zinc-700 shrink-0"
                    title="Remonter"
                  >
                    <GripVertical size={14} strokeWidth={1.5} />
                  </button>
                  <div className="w-[512px] shrink-0">
                    <SearchableSelect
                      items={catalogItems}
                      value={f.catalog_id}
                      onChange={(v) => updateField(i, { catalog_id: v })}
                      placeholder="Choisir une métadonnée…"
                      testId={`type-field-select-${i}`}
                    />
                  </div>
                  <label className="shrink-0 flex items-center gap-2 text-[11px] text-zinc-600 font-medium cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={f.required}
                      onChange={(e) => updateField(i, { required: e.target.checked })}
                    />
                    Obligatoire
                  </label>
                  <button
                    onClick={() => removeField(i)}
                    data-testid={`type-field-remove-${i}`}
                    className="shrink-0 text-zinc-400 hover:text-[#DC2626] transition-colors"
                  >
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-zinc-200 bg-zinc-50 flex justify-end gap-3 shrink-0">
          <button
            onClick={closePanel}
            className="border border-zinc-300 text-sm px-5 py-2 hover:bg-zinc-100 transition-colors"
          >
            Annuler
          </button>
          <button
            data-testid="type-save-btn"
            onClick={save}
            disabled={migrationLoading}
            className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-semibold px-6 py-2 transition-colors disabled:opacity-50"
          >
            <Save size={14} strokeWidth={1.5} />
            {migrationLoading ? "Vérification…" : isAdmin ? "Enregistrer" : "Soumettre"}
          </button>
        </div>
      </div>

      {/* ═══ MIGRATION DIALOG ═══ */}
      {migration && (
        <MigrationDialog
          preview={migration.preview}
          onCancel={() => setMigration(null)}
          onKeep={() => _performSave(migration.payload, false)}
          onMigrate={() => _performSave(migration.payload, true)}
        />
      )}
    </div>
  );
}

// ── Migration Dialog ─────────────────────────────────────────────────────────
function MigrationDialog({ preview, onCancel, onKeep, onMigrate }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div
        data-testid="migration-dialog"
        className="relative bg-white border border-zinc-200 shadow-2xl w-full max-w-xl mx-4"
      >
        <div className="px-6 py-4 border-b border-zinc-200 flex items-start gap-3">
          <AlertTriangle size={18} strokeWidth={1.5} className="text-[#EAB308] mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-zinc-900">
              {preview.folder_changed && preview.filename_changed
                ? "Modification du chemin et du nommage"
                : preview.filename_changed
                  ? "Modification du nommage du fichier"
                  : "Modification du chemin de classement"}
            </div>
            <div className="text-sm text-zinc-600 mt-0.5">
              {preview.affected_count} document(s) classé(s) utilisent ce type.
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-zinc-50 border-b border-zinc-200 space-y-3">
          {preview.folder_changed && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-1.5">Chemin du dossier</div>
              <div className="flex items-start gap-2 text-xs font-mono flex-wrap">
                <span className="line-through text-zinc-400 break-all">{preview.old_folder_template}</span>
                <ArrowRight size={12} strokeWidth={1.5} className="text-zinc-400 shrink-0 mt-0.5" />
                <span className="text-zinc-900 font-semibold break-all">{preview.new_folder_template}</span>
              </div>
            </div>
          )}
          {preview.filename_changed && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-1.5">Nommage du fichier</div>
              <div className="flex items-start gap-2 text-xs font-mono flex-wrap">
                <span className="line-through text-zinc-400 break-all">{preview.old_filename_template}</span>
                <ArrowRight size={12} strokeWidth={1.5} className="text-zinc-400 shrink-0 mt-0.5" />
                <span className="text-zinc-900 font-semibold break-all">{preview.new_filename_template}</span>
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-b border-zinc-200 grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle size={14} strokeWidth={1.5} className="text-green-600 shrink-0" />
            <span className="text-zinc-700">
              <span className="font-semibold text-zinc-900">{preview.migratable}</span> document(s) recalculable(s)
            </span>
          </div>
          {preview.incomplete > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle size={14} strokeWidth={1.5} className="text-[#EAB308] shrink-0" />
              <span className="text-zinc-700">
                <span className="font-semibold text-zinc-900">{preview.incomplete}</span> incomplet(s) — ignoré(s)
              </span>
            </div>
          )}
        </div>
        {preview.sample_paths.length > 0 && (
          <div className="px-6 py-4 border-b border-zinc-200 max-h-56 overflow-y-auto">
            <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-3">Aperçu</div>
            <div className="space-y-3">
              {preview.sample_paths.map((s, i) => (
                <div key={i} className="text-[11px] font-mono space-y-1">
                  <div className="text-zinc-500 break-all">{s.filename}</div>
                  {preview.folder_changed && (
                    <div>
                      <span className="text-[9px] uppercase tracking-widest text-zinc-400 block mb-0.5">dossier</span>
                      <div className="flex items-start gap-1.5 flex-wrap">
                        <span className="text-zinc-400 line-through break-all">{s.old_path}</span>
                        <ArrowRight size={10} strokeWidth={1.5} className="text-zinc-400 shrink-0 mt-0.5" />
                        <span className={`break-all ${s.can_migrate ? "text-zinc-800" : "text-[#EAB308]"}`}>
                          {s.new_path}
                        </span>
                      </div>
                    </div>
                  )}
                  {preview.filename_changed && (
                    <div>
                      <span className="text-[9px] uppercase tracking-widest text-zinc-400 block mb-0.5">fichier</span>
                      <div className="flex items-start gap-1.5 flex-wrap">
                        <span className="text-zinc-400 line-through break-all">{s.old_filename}</span>
                        <ArrowRight size={10} strokeWidth={1.5} className="text-zinc-400 shrink-0 mt-0.5" />
                        <span className={`break-all ${s.can_migrate ? "text-zinc-800" : "text-[#EAB308]"}`}>
                          {s.new_filename}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="px-6 py-4 flex flex-col sm:flex-row gap-2 justify-end">
          <button
            data-testid="migration-cancel"
            onClick={onCancel}
            className="border border-zinc-300 text-sm px-4 py-2 hover:bg-zinc-100 transition-colors"
          >
            Annuler
          </button>
          <button
            data-testid="migration-keep"
            onClick={onKeep}
            className="border border-zinc-400 text-sm px-4 py-2 hover:bg-zinc-100 transition-colors"
          >
            Laisser les documents en place
          </button>
          <button
            data-testid="migration-recalculate"
            onClick={onMigrate}
            disabled={preview.migratable === 0}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium px-5 py-2 transition-colors disabled:opacity-40"
          >
            Recalculer les chemins ({preview.migratable})
          </button>
        </div>
      </div>
    </div>
  );
}

function ZoneTitle({ number, label }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[9px] font-mono text-zinc-500 tabular-nums">{number}</span>
      <span className="text-[11px] uppercase tracking-[0.2em] font-mono font-semibold text-zinc-800">
        {label}
      </span>
      <div className="flex-1 border-t border-zinc-300" />
    </div>
  );
}

function InlineField({ label, children }) {
  return (
    <div className="flex items-center gap-4">
      <span className="text-[11px] uppercase tracking-[0.12em] font-mono text-zinc-600 w-24 shrink-0 text-right font-medium">
        {label}
      </span>
      {children}
    </div>
  );
}

function PlaceholderChips({ label, tokens, active, onToggle, testPrefix }) {
  if (tokens.length === 0) return null;
  return (
    <div className="mt-2.5">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5 font-medium">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {tokens.map((t) => {
          const token = `{${t.key}}`;
          const isActive = active.includes(token);
          return (
            <button
              key={t.key}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onToggle(t.key)}
              data-testid={`${testPrefix}-${t.key}`}
              className={`inline-flex items-center border text-[10px] px-2.5 py-1 font-sans transition-colors ${
                isActive
                  ? "border-[#002FA7] bg-[#002FA7] text-white"
                  : "border-zinc-300 bg-white text-zinc-700 hover:border-[#002FA7] hover:text-[#002FA7]"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
